import {
	type LegacySegmentEventPropMappings,
	type LegacySegmentTrackEvent,
} from '@1debit/chime-schemas/typescript_web_analytics';

import { type Environment, getTargetOrigin } from './utils/environments';

type SegmentAnalyticsIframeMessage = 'event' | 'page' | 'identify' | 'setAnonymousId' | 'init' | 'reset';

export const IFRAME_ID = 'chime-segment-analytics';

export type SegmentHandlerConfig = {
	env: Environment;
	segmentKey: string;
	deviceId: string;
};

export class SegmentHandler {
	private targetOrigin!: string;
	private deviceId!: string;
	private segmentKey!: string;
	private analyticsIframe: HTMLIFrameElement | null = null;
	private isInitialized = false;
	private eventCallbacksQueue: Array<VoidFunction> = [];

	constructor({ env, segmentKey, deviceId }: SegmentHandlerConfig) {
		this.segmentKey = segmentKey;
		this.targetOrigin = getTargetOrigin(env);
		this.deviceId = deviceId;
	}

	private sendDataToAnalyticsIframe(message: SegmentAnalyticsIframeMessage, properties: Record<string, unknown>): void {
		// Sometimes the pointer stored in the instance of this class is undefined, so we manually fetch to ensure the iframe is present in the DOM
		const iframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement;
		const iframeContentWindow = iframe?.contentWindow;

		iframeContentWindow?.postMessage(
			{ message, properties: { ...properties, windowState: window.history.state, deviceId: this.deviceId } },
			this.targetOrigin,
		);
	}

	private formatUniqueId(uniqueId: string): string {
		return uniqueId.trim().toLowerCase().replace(/\s/g, '_');
	}

	// Some functions might be called before the iframe is loaded. We need to enqueue them and send them once the iframe is loaded.
	private enqueueOrSendEvent(sendEventCallback: VoidFunction): void {
		if (this.isInitialized) {
			sendEventCallback();
		} else {
			this.eventCallbacksQueue.push(sendEventCallback);
		}
	}

	// Some events might have been enqueued before the document was fully loaded. We need to send them once we make sure analytics is initialized.
	private sendQueuedEvents(): void {
		this.eventCallbacksQueue.forEach(eventCallback => eventCallback());
		this.eventCallbacksQueue = [];
	}

	private checkIfSegmentIsInitialized(event: MessageEvent): void {
		if (event.data === 'iframe initialized') {
			this.isInitialized = true;
			this.sendQueuedEvents();
			this.cleanUpIframeMessageListener();
		}
	}

	private cleanUpIframeMessageListener(): void {
		window.removeEventListener('message', this.checkIfSegmentIsInitialized);
	}

	init(segmentIframeElement?: HTMLIFrameElement): void {
		if (this.isInitialized) {
			console.warn(
				'Analytics has already been initialized. Investigate whether Analytics.init is called correctly in the AnalyticsProvider',
			);
			return;
		}
		const analyticsIframe = segmentIframeElement ?? document.getElementById(IFRAME_ID);
		if (!analyticsIframe) {
			console.error('Analytics iframe not found in the DOM.');
		}
		this.analyticsIframe = analyticsIframe as HTMLIFrameElement;

		this.sendDataToAnalyticsIframe('init', {
			segmentKey: this.segmentKey,
			id: this.deviceId,
			path: window.location.pathname,
		});

		window.addEventListener('message', this.checkIfSegmentIsInitialized.bind(this));
	}

	identify(userId: number, properties?: Record<string, unknown>): void {
		this.enqueueOrSendEvent(() => this.sendDataToAnalyticsIframe('identify', { userId, ...properties }));
	}

	trackPage(pageName: string, properties?: Record<string, unknown>): void {
		const search = document.location.search ?? '';
		const referrer = document.referrer ?? '';
		const url = document.location.href;
		const path = document.location.pathname;

		this.enqueueOrSendEvent(() =>
			this.sendDataToAnalyticsIframe('page', {
				pageName,
				search,
				referrer,
				url,
				path,
				...properties,
			}),
		);
	}

	reset(): void {
		this.enqueueOrSendEvent(() => this.sendDataToAnalyticsIframe('reset', {}));
	}

	trackEvent<EventName extends LegacySegmentTrackEvent>(
		eventName: EventName,
		properties: LegacySegmentEventPropMappings[LegacySegmentTrackEvent],
	): void {
		this.enqueueOrSendEvent(() =>
			this.sendDataToAnalyticsIframe('event', {
				eventName,
				...properties,
				unique_id: this.formatUniqueId(properties.unique_id),
			}),
		);
	}
}
